import { Link } from "react-router";
import {
  Users,
  Calendar,
  FolderOpen,
  Pill,
  Package,
  FileText,
  Compass,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import type { OrganizationType } from "@bookdental/schemas";
import { Card, CardContent } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { getOrganizationTypeLabel } from "@/components/onboarding/constants";

const CLINIC_LINKS = [
  {
    href: "/pacientes",
    icon: Users,
    title: "Cadastrar primeiro paciente",
    description: "Comece sua base de pacientes e historicos clinicos.",
  },
  {
    href: "/agenda",
    icon: Calendar,
    title: "Configurar agenda",
    description: "Defina profissionais, horarios e tipos de atendimento.",
  },
  {
    href: "/receituario",
    icon: Pill,
    title: "Modelos de receituario",
    description: "Prepare receituarios e atestados prontos pra usar.",
  },
  {
    href: "/arquivos",
    icon: FolderOpen,
    title: "Organizar arquivos",
    description: "Crie pastas para DICOM, fotografias e documentos.",
  },
];

const LAB_LINKS = [
  {
    href: "/lab/cadastros",
    icon: Users,
    title: "Cadastrar clinicas parceiras",
    description: "Cadastre os dentistas e clinicas que enviam casos.",
  },
  {
    href: "/procedimentos",
    icon: Package,
    title: "Cadastrar servicos",
    description: "Defina os trabalhos e planejamentos que voce oferece.",
  },
  {
    href: "/lab/producao",
    icon: FileText,
    title: "Acompanhar producao",
    description: "Organize casos em andamento, entregas e prazos.",
  },
  {
    href: "/lab/arquivos",
    icon: FolderOpen,
    title: "Organizar arquivos",
    description: "Crie pastas para escaneamentos, fotos e planejamentos.",
  },
];

function useQuickLinks(types: string[]) {
  const isClinic = types.includes("DENTAL_OFFICE");
  const isLab = types.includes("DENTAL_LAB");
  if (isClinic && isLab) return [...CLINIC_LINKS, ...LAB_LINKS];
  if (isLab) return LAB_LINKS;
  return CLINIC_LINKS;
}

export function Dashboard() {
  const { data: me, isLoading } = trpc.me.current.useQuery();

  const firstName = me?.name?.split(" ")[0] ?? "";
  const types = me?.activeOrganization?.types ?? [];
  const isPersonal = !!me?.activeOrganization?.isPersonal;
  const organizationTypeDesc = types
    .map((t) => getOrganizationTypeLabel(t as OrganizationType, isPersonal))
    .join(" + ");
  const quickLinks = useQuickLinks(types);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <div
        className={[
          "relative overflow-hidden rounded-3xl p-8 md:p-10",
          // Light: gradient lilás suave
          "border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-white",
          // Dark: card sólido com borda branco-translúcido (Linear/Vercel style),
          // ring interno sutil e shadow profunda pra destacar do fundo preto
          "dark:border-white/[0.06] dark:bg-[#121216] dark:from-transparent dark:via-transparent dark:to-transparent",
          "dark:shadow-2xl dark:shadow-black/40 dark:ring-1 dark:ring-inset dark:ring-white/[0.03]",
        ].join(" ")}
      >
        {/* Blob decorativo — light: azul claro / dark: glow azul vibrante */}
        <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-brand-200/40 blur-3xl dark:bg-primary/25" />
        {/* Segundo blob só no dark, canto oposto, pra dar profundidade */}
        <div className="absolute -bottom-20 -left-10 hidden h-56 w-56 rounded-full bg-brand-500/10 blur-3xl dark:block" />
        {/* Highlight sutil no topo — glass effect no dark */}
        <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-transparent via-white/20 to-transparent dark:block" />

        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/70 px-3.5 py-1 text-xs font-semibold text-brand-700 backdrop-blur dark:border-primary/30 dark:bg-primary/15 dark:text-primary dark:shadow-[0_0_20px_-4px_hsl(var(--primary)/0.3)]">
            <Sparkles className="h-3.5 w-3.5" />
            Bem-vindo(a) ao DentalBook
          </div>
          <h1 className="mt-5 text-3xl font-bold text-zinc-900 dark:text-foreground sm:text-4xl">
            {isLoading
              ? "Carregando..."
              : firstName
                ? `Ola, ${firstName} 👋`
                : "Ola 👋"}
          </h1>
          <p className="mt-3 max-w-2xl leading-relaxed text-zinc-600 dark:text-zinc-300">
            {types.length > 0 ? (
              <>
                Sua{" "}
                <strong className="font-semibold text-zinc-900 dark:text-primary">
                  {organizationTypeDesc}
                </strong>{" "}
                esta pronta. Aqui voce vai centralizar agenda, pacientes, arquivos
                e o visualizador DICOM 3D.
              </>
            ) : (
              "Defina sua area de atuacao em Meu perfil pra personalizar seus primeiros passos."
            )}
          </p>
        </div>
      </div>

      <section>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-zinc-500">
          Primeiros passos
        </h2>
        {types.length === 0 ? (
          <Link to="/configuracoes/atuacao" className="group block">
            <Card className="transition-all hover:border-brand-200 hover:shadow-md">
              <CardContent className="flex items-start gap-4 p-6">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 transition-colors group-hover:bg-brand-900 group-hover:text-white">
                  <Compass className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-zinc-900">
                    Defina sua area de atuacao
                  </h3>
                  <p className="mt-1 text-sm text-zinc-500 leading-relaxed">
                    Em Meu perfil, escolha Clinica e/ou Laboratorio pra personalizar seu menu e
                    seus primeiros passos.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-zinc-300 group-hover:text-brand-700 transition-colors" />
              </CardContent>
            </Card>
          </Link>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.href} to={link.href} className="group">
                  <Card className="transition-all hover:border-brand-200 hover:shadow-md">
                    <CardContent className="flex items-start gap-4 p-6">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 transition-colors group-hover:bg-brand-900 group-hover:text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-[15px] font-semibold text-zinc-900">{link.title}</h3>
                        <p className="mt-1 text-sm text-zinc-500 leading-relaxed">
                          {link.description}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-zinc-300 group-hover:text-brand-700 transition-colors" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-zinc-500">
          Proximos recursos
        </h2>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-zinc-500 leading-relaxed">
              A plataforma esta sendo montada modulo a modulo. Os itens do menu lateral
              representam as funcoes que chegarao em breve: agendamento com IA, WhatsApp,
              prontuario digital, receituario, visualizador DICOM 3D e modo academico para
              professores e estudantes.
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
